import {Chip} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React, {useState} from "react";
import {apiRetrieveImproved} from "../../../api";
import {config} from "../../../config/config.ts";
import {SportTypes} from "../../../constants.ts";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/event-pass-feedback')({
    component: RouteComponent,
})

// Mirrors MasterEventPassFeedbackController::index.
type Row = {
    id: number;
    organizer_uid: string;
    organizer_name: string;
    organizer_email: string | null;
    club_uid: string | null;
    event_uid: string;
    event_type: 'tournament' | 'league';
    event_title: string | null;
    event_slug: string | null;
    sport_type: string | null;
    price_cents: number | null;
    reason: Reason | null;
    comment: string | null;
    gate_seen_at: string;
    email_sent_at: string;
    responded_at: string | null;
    purchased: boolean;
    purchased_at: string | null;
    purchased_cents: number | null;
    pass_kind: string | null;
    purchased_via_email: boolean;
};
type Reason = 'setup' | 'price' | 'testing' | 'other';
type Summary = {sent: number; responses: number; response_rate_pct: number | null; by_reason: Record<Reason, number>; purchased: number; purchased_via_email: number};
type Page = {from: string; to: string; enabled: boolean; rows: Row[]; has_more: boolean; summary: Summary};

const REASONS: {value: Reason; label: string}[] = [
    {value: 'setup',   label: "Setup isn't working"},
    {value: 'price',   label: 'Price too high'},
    {value: 'testing', label: 'Just testing'},
    {value: 'other',   label: 'Something else'},
];
const reasonLabel = (r: Reason | null) => REASONS.find((x) => x.value === r)?.label ?? '—';
const iso = (d: Date) => d.toISOString().slice(0, 10);
const when = (s: string | null) => s ? new Date(s.replace(' ', 'T')).toLocaleString(undefined, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}) : '—';
const money = (cents: number | null) => cents === null ? '' : `$${(cents / 100).toFixed(2)}`;

type YesNo = '' | '1' | '0';
function YesNoPills({label, value, onChange, yes = 'Yes', no = 'No'}: {label: string; value: YesNo; onChange: (v: YesNo) => void; yes?: string; no?: string}) {
    const pill = (v: YesNo, text: string) => (
        <button key={v} type="button" onClick={() => onChange(v)}
                className={'rounded-full px-3 py-1 text-xs font-semibold transition ' + (v === value ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200')}>
            {text}
        </button>
    );
    return (
        <div className="flex items-center gap-1.5">
            <span className="mr-1 text-xs text-default-500">{label}</span>
            {pill('', 'All')}{pill('1', yes)}{pill('0', no)}
        </div>
    );
}

/**
 * Master → Event Pass feedback: every event the follow-up email went to, newest first, with the
 * organiser, the one-click reason and comment they sent back (blank until they do), when the email
 * went, and whether a pass was bought later — and whether through the email's button. "Setup"
 * answers carry a badge: those need a personal reply.
 */
function RouteComponent() {
    const [from, setFrom] = useState(() => iso(new Date(Date.now() - 30 * 86400_000)));
    const [to, setTo] = useState(() => iso(new Date()));
    const [reason, setReason] = useState<'' | Reason>('');
    const [responded, setResponded] = useState<YesNo>('');
    const [purchased, setPurchased] = useState<YesNo>('');
    const [viaEmail, setViaEmail] = useState<YesNo>('');

    const [offset, setOffset] = useState(0);
    const [accumulated, setAccumulated] = useState<Row[]>([]);
    React.useEffect(() => { setOffset(0); setAccumulated([]); }, [from, to, reason, responded, purchased, viaEmail]);

    const query = useQuery<Page>({
        queryKey: ['master-event-pass-feedback', from, to, reason, responded, purchased, viaEmail, offset],
        queryFn:  () => apiRetrieveImproved('master/event-pass-feedback' as any, {
            from, to, offset, limit: 25,
            reason: reason || undefined, responded: responded || undefined, purchased: purchased || undefined, via_email: viaEmail || undefined,
        } as any),
    });
    React.useEffect(() => {
        if (!query.data) return;
        setAccumulated((prev) => {
            if (offset === 0) return query.data!.rows;
            const seen = new Set(prev.map((r) => r.id));
            return [...prev, ...query.data!.rows.filter((r) => !seen.has(r.id))];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data]);
    const rows = accumulated.length || offset > 0 ? accumulated : (query.data?.rows || []);
    const s = query.data?.summary;

    const stat = (label: string, value: React.ReactNode, hint?: string) => (
        <div className="rounded-xl border border-default-200 bg-content1 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-default-500">{label}</div>
            <div className="mt-0.5 text-2xl font-bold tabular-nums">{value}</div>
            {hint && <div className="text-xs text-default-400">{hint}</div>}
        </div>
    );

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-7xl py-4">
                <h1 className="mb-1 text-2xl font-bold">Event Pass feedback</h1>
                <p className="mb-3 text-sm text-default-500">
                    Every event whose organiser was emailed an hour after first seeing the pass modal without buying ("Quick question about …"),
                    newest first. The reason and comment arrive when they submit the feedback page; opening a link records nothing.
                    "Purchased later" reads the event's own pass; "via email button" means the checkout was started from the email.
                    {query.data && !query.data.enabled && <span className="ml-1 font-semibold text-warning">The email is switched off (Master → Activation).</span>}
                </p>

                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <label className="text-xs text-default-500">Sent from <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="ml-1 rounded-md border border-default-200 px-2 py-1 text-sm" /></label>
                    <label className="text-xs text-default-500">to <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="ml-1 rounded-md border border-default-200 px-2 py-1 text-sm" /></label>
                </div>

                {s && (
                    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
                        {stat('Emails sent', s.sent)}
                        {stat('Responses', s.responses, s.response_rate_pct === null ? undefined : `${s.response_rate_pct}% response rate`)}
                        {REASONS.map((r) => stat(r.label, s.by_reason?.[r.value] ?? 0))}
                        {stat('Bought later', s.purchased, s.sent > 0 ? `${Math.round(s.purchased * 100 / s.sent)}% of emailed` : undefined)}
                        {stat('Via email button', s.purchased_via_email)}
                    </div>
                )}

                <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <div className="flex items-center gap-1.5">
                        <span className="mr-1 text-xs text-default-500">Reason</span>
                        {[{value: '' as const, label: 'All'}, ...REASONS].map((r) => (
                            <button key={r.value} type="button" onClick={() => setReason(r.value)}
                                    className={'rounded-full px-3 py-1 text-xs font-semibold transition ' + (r.value === reason ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200')}>
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <YesNoPills label="Responded" value={responded} onChange={setResponded} />
                    <YesNoPills label="Bought later" value={purchased} onChange={setPurchased} />
                    <YesNoPills label="Via email button" value={viaEmail} onChange={setViaEmail} />
                </div>

                {query.isLoading && offset === 0 ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-12 text-center text-default-500">No follow-up emails match.</div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-default-200">
                        <table className="w-full min-w-[1280px] text-sm">
                            <thead>
                                <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                    <th className="px-3 py-2">Organiser</th>
                                    <th className="px-3 py-2">Event</th>
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2">Reason</th>
                                    <th className="px-3 py-2">Comment</th>
                                    <th className="px-3 py-2">Email sent</th>
                                    <th className="px-3 py-2">Responded</th>
                                    <th className="px-3 py-2">Purchased later?</th>
                                    <th className="px-3 py-2">Via email button?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className={'border-b border-default-100 last:border-0 ' + (r.reason === 'setup' ? 'bg-warning-50/40 dark:bg-warning-900/10' : '')}>
                                        <td className="max-w-[220px] px-3 py-2">
                                            <div className="truncate font-medium" title={r.organizer_name}>{r.organizer_name || <span className="text-default-400">(no name)</span>}</div>
                                            <div className="truncate text-xs text-default-500" title={r.organizer_email ?? ''}>{r.organizer_email ?? '—'}</div>
                                        </td>
                                        <td className="max-w-[240px] px-3 py-2">
                                            <a href={`${config.publicUrl}/e/${r.event_slug || r.event_uid}`} target="_blank" rel="noreferrer"
                                               className="block truncate font-medium text-primary hover:underline" title={r.event_title || r.event_uid}>
                                                {r.event_title || <span className="text-default-400">(untitled)</span>}
                                            </a>
                                            <div className="truncate text-xs text-default-500">
                                                {(SportTypes as any)[r.sport_type || ''] || r.sport_type || ''}{r.price_cents !== null ? ` · quoted ${money(r.price_cents)}` : ''}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <Chip size="sm" variant="flat" color={r.event_type === 'tournament' ? 'secondary' : 'primary'}>{r.event_type === 'tournament' ? 'Tournament' : 'League'}</Chip>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {r.reason === 'setup'
                                                ? <Chip size="sm" color="warning" variant="solid" title="Needs a personal follow-up">Setup isn't working · follow up</Chip>
                                                : r.reason ? <Chip size="sm" variant="flat">{reasonLabel(r.reason)}</Chip> : <span className="text-default-400">—</span>}
                                        </td>
                                        <td className="max-w-[320px] px-3 py-2 text-default-700">
                                            {r.comment ? <span className="line-clamp-3 whitespace-pre-line" title={r.comment}>{r.comment}</span> : <span className="text-default-400">—</span>}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">{when(r.email_sent_at)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">{when(r.responded_at)}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {r.purchased
                                                ? <span className="font-semibold text-success">Yes <span className="text-xs font-normal text-default-500">{when(r.purchased_at)}{r.purchased_cents ? ` · ${money(r.purchased_cents)}` : r.pass_kind ? ` · ${r.pass_kind}` : ''}</span></span>
                                                : <span className="text-default-400">No</span>}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {r.purchased_via_email ? <span className="font-semibold text-success">Yes</span> : <span className="text-default-400">No</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {query.data?.has_more && (
                    <div className="mt-3 flex justify-center">
                        <button type="button" onClick={() => setOffset(offset + 25)} disabled={query.isFetching}
                                className="rounded-full bg-default-100 px-4 py-1.5 text-sm font-semibold text-default-700 hover:bg-default-200 disabled:opacity-50">
                            {query.isFetching ? 'Loading…' : 'Show more'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
